import { createContext, useContext, useEffect, useState } from 'react';
import type {
  SafeAreaInsets,
  TelegramContextType,
  ThemeParams,
  WebApp,
} from '../../types/telegram';
import { useTelegramUserStore } from '../../store/useTelegramUserStore';

const TELEGRAM_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js?57';

declare global {
  interface Window {
    Telegram?: {
      WebApp: WebApp;
    };
  }
}

export const TelegramContext = createContext<TelegramContextType>({
  webApp: null,
  userId: null,
  launchParams: {},
  isAllowed: false,
  platform: null,
  themeParams: null,
  setTheme: () => {},
  exitFullscreen: () => {},
  isFullscreen: false,
  isTelegram: false,
  systemInsets: { top: 0, bottom: 0, left: 0, right: 0 },
  contentInsets: { top: 0, bottom: 0, left: 0, right: 0 },
});

export const useTelegram = () => {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
};

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [webApp, setWebApp] = useState<WebApp | null>(null);
  const [isAllowed, setIsAllowed] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const [launchParams, setLaunchParams] = useState<Record<string, unknown>>({});
  const [themeParams, setThemeParams] = useState<ThemeParams | null>(null);
  const [isTelegram, setIsTelegram] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [systemInsets, setSystemInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const [contentInsets, setContentInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });
  const setUser = useTelegramUserStore((state) => state.setUser);

  const applyBodyInsets = (top: number, bottom: number) => {
    const root = document.getElementById('root');
    if (!root) {
      return;
    }

    root.style.paddingTop = `${top}px`;
    root.style.paddingBottom = `${bottom}px`;
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.minHeight = '100vh';
  };

  const setTheme = (newTheme: Partial<ThemeParams>) => {
    setThemeParams((prev) => (prev ? { ...prev, ...newTheme } : null));
    if (!window.Telegram?.WebApp) {
      return;
    }

    Object.entries(newTheme).forEach(([key, value]) => {
      if (value && key === 'headerBackgroundColor') {
        window.Telegram?.WebApp?.setHeaderColor(value as `#${string}`);
      }
    });
  };

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg && (tg.platform === 'ios' || tg.platform === 'android')) {
      tg.requestFullscreen();
    }
  }, []);

  const exitFullscreen = () => {
    const tg = window.Telegram?.WebApp;
    if (!tg) {
      return;
    }

    try {
      if (typeof tg.exitFullscreen === 'function') {
        tg.exitFullscreen();
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  useEffect(() => {
    const top = systemInsets.top + contentInsets.top;
    const bottom = systemInsets.bottom + contentInsets.bottom;
    applyBodyInsets(top, bottom);

    const handleResize = () => applyBodyInsets(top, bottom);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [systemInsets, contentInsets]);

  useEffect(() => {
    let mounted = true;
    let scriptNode: HTMLScriptElement | null = null;
    const tg = window.Telegram?.WebApp;

    const initTelegram = () => {
      const webAppInstance = window.Telegram?.WebApp;
      if (!mounted || !webAppInstance) {
        return;
      }

      const hasUser = Boolean(webAppInstance.initDataUnsafe?.user);
      setPlatform(webAppInstance.platform ?? null);
      setLaunchParams((webAppInstance.initDataUnsafe || {}) as Record<
        string,
        unknown
      >);
      setThemeParams((webAppInstance.themeParams as ThemeParams) || null);
      setIsFullscreen(webAppInstance.isFullscreen);

      if (!hasUser) {
        setWebApp(null);
        setIsTelegram(false);
        setIsAllowed(false);
        setUserId(null);
        return;
      }

      setWebApp(webAppInstance);
      setIsTelegram(true);
      setIsAllowed(true);

      const user = webAppInstance.initDataUnsafe?.user;
      if (user) {
        setUserId(user.id);
        setUser(user);
      }

      const isDesktopPlatform =
        webAppInstance.platform === 'tdesktop' ||
        webAppInstance.platform === 'macos' ||
        webAppInstance.platform === 'web';

      const initialSystemInsets: SafeAreaInsets = {
        top: isDesktopPlatform ? 24 : webAppInstance.platform === 'ios' ? 47 : 24,
        bottom:
          isDesktopPlatform ? 24 : webAppInstance.platform === 'ios' ? 34 : 23,
        left: 0,
        right: 0,
      };
      const initialContentInsets: SafeAreaInsets = {
        top: isDesktopPlatform ? 24 : 56,
        bottom: 0,
        left: 0,
        right: 0,
      };

      setSystemInsets(initialSystemInsets);
      setContentInsets(initialContentInsets);
      applyBodyInsets(
        initialSystemInsets.top + initialContentInsets.top,
        initialSystemInsets.bottom + initialContentInsets.bottom,
      );

      webAppInstance.onEvent('fullscreenChanged', () => {
        setIsFullscreen(webAppInstance.isFullscreen);
      });

      webAppInstance.onEvent(
        'fullscreenFailed',
        (event: { error: 'UNSUPPORTED' | 'ALREADY_FULLSCREEN' }) => {
          console.error('Fullscreen failed:', event.error);
        },
      );

      webAppInstance.setBackgroundColor('#121212');
      webAppInstance.setHeaderColor('#121212');
      webAppInstance.disableVerticalSwipes();
      webAppInstance.expand();
      webAppInstance.ready();
    };

    if (tg) {
      initTelegram();
    } else {
      scriptNode = document.createElement('script');
      scriptNode.src = TELEGRAM_SCRIPT_SRC;
      scriptNode.async = true;
      scriptNode.onload = initTelegram;
      document.head.appendChild(scriptNode);
    }

    return () => {
      mounted = false;
      if (scriptNode && scriptNode.parentNode) {
        scriptNode.parentNode.removeChild(scriptNode);
      }
    };
  }, [setUser]);

  return (
    <TelegramContext.Provider
      value={{
        webApp,
        userId,
        launchParams,
        isAllowed,
        platform,
        themeParams,
        setTheme,
        exitFullscreen,
        isFullscreen,
        isTelegram,
        systemInsets,
        contentInsets,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
};
